import { formatDateTime, getGMTTimezone } from '@elo-kit/utils/dateTime.utils'
import { Country } from 'shared/api/countries.api'
import { Address } from 'types/address'

import { getCountryName } from './countries.utils'
import { isNumber } from './validators.utils'

export const getTicketLimit = (limit) => (isNumber(limit) ? limit : I18n.t('react.cabinet.common.unlimited'))

interface Ticket {
  id: number
  name: string
  online: boolean
  locationShortName: string
  address?: Address
}

interface TicketDate {
  id: number
  startDate: string
  timeZoneName: string
  endDate: string
  additionalText: string
  expired?: boolean
}

export const getFormattedTicketLocation = (ticket: Ticket, countries: Country[]) => {
  const { online, locationShortName = '', address } = ticket || {}

  if (online) {
    return I18n.t('react.shared.online_location')
  }

  if (address) {
    const parts = [
      address.line1,
      address.postalCode,
      address.city,
      getCountryName(countries, address.countryCode),
    ].filter(Boolean)
    return locationShortName ? `${parts.join(', ')} (${locationShortName})` : parts.join(', ')
  }

  return locationShortName
}

export const getLocationShortName = (ticket: Ticket) => {
  const { online, locationShortName = '' } = ticket || {}
  if (online) return ''
  return !!locationShortName ? `(${locationShortName})` : ''
}

export const getFormattedUpgradableTicketLocation = (ticket: Ticket, countries: Country[]) => {
  const { online, address } = ticket || {}

  if (online) {
    return I18n.t('react.shared.online_location')
  }

  if (address) {
    const parts = [
      address.line1,
      address.postalCode,
      address.city,
      getCountryName(countries, address.countryCode),
    ].filter(Boolean)
    return parts.join(', ')
  }
}

export const getFormattedTicketLabel = (ticket: Ticket, countries: Country[]) => {
  const { id, name, online, address } = ticket || {}
  const locationShortName = getLocationShortName(ticket)

  if (online) {
    return `${id} - ${name} ${locationShortName} - ${I18n.t('react.shared.online_location')}`
  }

  if (address) {
    const parts = [
      address.line1,
      address.postalCode,
      address.city,
      getCountryName(countries, address.countryCode),
    ].filter(Boolean)
    return `${id} - ${name} ${locationShortName} - ${parts.join(', ')}`
  }

  return `${id} - ${name} ${locationShortName}`
}

export const getFormattedTicketDates = (ticketDate: TicketDate) => {
  const { startDate, timeZoneName, endDate, additionalText } = ticketDate || {}

  if (!(startDate || endDate)) {
    return additionalText || ''
  }

  if (startDate && endDate) {
    return `${formatDateTime(startDate, null, timeZoneName)} - ${formatDateTime(
      endDate,
      null,
      timeZoneName
    )} ${getGMTTimezone(timeZoneName)}`
  }

  if (startDate) {
    return `${formatDateTime(startDate, null, timeZoneName)} ${getGMTTimezone(timeZoneName)}`
  }

  return ''
}

export const getFormattedTicketDate = (ticketDate: TicketDate): string | { dateStart; dateEnd; startTime; endTime } => {
  const { startDate, timeZoneName, endDate, additionalText } = ticketDate || {}

  if (!(startDate || endDate)) {
    return additionalText || ''
  }

  const [dateStart, startTime] = formatDateTime(startDate, null, timeZoneName).split(' ')
  const [dateEnd, endTime] = formatDateTime(endDate, null, timeZoneName).split(' ')

  return {
    dateStart,
    dateEnd,
    startTime,
    endTime,
  }
}

export const getNotExpiredTicketDates = (list) => (list || []).filter((item) => !item.expired)

export const formatTicketDatesOptions = (list = [], withId = false) => {
  const results = []

  list.forEach((item) => {
    if (item.startDate) {
      const label = getFormattedTicketDates(item)

      results.push({
        label: withId ? `${item.id} - ${label}` : label,
        value: item.id,
      })
    } else if (withId && item.id) {
      results.push({
        label: item.id,
        value: item.id,
      })
    }
  })

  return results
}
